import { useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router'
import { AppShell } from '../../app/AppShell'
import { Button, Card, SectionLabel } from '../../design/components'
import { useCampaignWorld } from '../../domains/campaigns/queries'
import { ComposerScreen } from '../../domains/composer/ComposerScreen'
import type { LocationImageType } from '../../domains/composer/types'
import styles from '../../domains/composer/composer.module.css'

/**
 * The location workbench: draw a place, or draw a map of it.
 *
 * Entered from the gallery with the type already chosen, because the two are different jobs and
 * the gallery is where you know which one you want. Unlike the Blazor composer the choice is not
 * locked for the session — a draft records the type it was drawn under, so there is nothing to
 * protect by locking it.
 */
export function LocationComposerPage() {
  const params = useParams<{ id: string; locationId: string }>()
  const campaignId = Number(params.id)
  const locationId = Number(params.locationId)

  const [search] = useSearchParams()
  const [imageType, setImageType] = useState<LocationImageType>(
    search.get('type') === 'BattleMap' ? 'BattleMap' : 'Depiction',
  )

  const world = useCampaignWorld(campaignId)
  const location = (world.data?.sections.locations?.locations ?? []).find(
    (candidate) => candidate.id === locationId,
  )
  const isGameMaster = world.data?.isGameMaster === true

  if (Number.isNaN(campaignId) || Number.isNaN(locationId)) {
    return (
      <AppShell title="Compose">
        <Card>
          <p className={styles.note}>That is not a place.</p>
        </Card>
      </AppShell>
    )
  }

  const isMap = imageType === 'BattleMap'

  return (
    <ComposerScreen
      owner={{ kind: 'location', campaignId, locationId }}
      subjectName={location?.name ?? 'Place'}
      subtitle={isMap ? 'BATTLE MAP' : 'DEPICTION'}
      canCompose={isGameMaster}
      requestExtras={{ imageType }}
      settingsSummaryPrefix={isMap ? 'Battle map' : 'Depiction'}
      /* The advice that changes what you get, on the card rather than behind the help icon.
         Both of these are learned the hard way otherwise — a depiction full of strangers that
         no comic panel can reuse, or a map of a building that is not the one in the picture. */
      hint={
        isMap ? (
          <p className={styles.hint}>
            <strong>Start from the depiction.</strong> Add this place&apos;s image with{' '}
            <em>This place&apos;s images</em> and the map will read as the same building — same
            footprint, same furniture, same exits.
          </p>
        ) : (
          <p className={styles.hint}>
            <strong>Leave the people out.</strong> Comic panels put your own characters in this
            location later, and every figure the model draws here is one that has to be worked
            around. Draw the room, not the scene.
          </p>
        )
      }
      extraSettings={
        <label className={styles.field}>
          <SectionLabel tone="muted" micro>
            Type
          </SectionLabel>
          <select
            className={styles.select}
            value={imageType}
            onChange={(event) => setImageType(event.target.value as LocationImageType)}
          >
            <option value="Depiction">Depiction</option>
            <option value="BattleMap">Battle map</option>
          </select>
          <p className={styles.note}>
            Not a filing decision — it picks the instructions the model draws under. A map comes out
            black and white, A4 landscape, with numbered zones and a legend.
          </p>
        </label>
      }
      generateWhat={
        isMap
          ? 'Sends the prompt above, in order, as a top-down battle map. The result arrives as a draft; nothing is saved until you save it.'
          : 'Sends the prompt above, in order. The result arrives as a draft; nothing is saved until you save it.'
      }
      generateAction={isMap ? 'Generate a battle map' : 'Generate an image'}
      downloadName={isMap ? 'battle-map' : 'depiction'}
      back={
        <Link to={`/campaigns/${campaignId}/locations/${locationId}/images`}>
          <Button variant="secondary" fullWidth>
            Back to the gallery
          </Button>
        </Link>
      }
    />
  )
}
